import {
  Checkbox,
  Divider,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  ChangeEvent,
  FormEvent,
  useCallback,
  useEffect,
  useState,
} from "react";
import { useGetCouponsForProductQuery } from "src/api/services/couponsApi";
import { useUpdatePriceMutation } from "src/api/services/pricesApi";
import { ProductPrice } from "src/api/types";
import { useSentryLogger } from "src/hooks/useSentryLogger";
import DrawerFieldRow from "src/shared/DrawerFieldRow";
import DrawerForm from "src/shared/DrawerForm";

interface Props {
  open: boolean;
  onClose: () => void;
  price: ProductPrice;
}

const UpdatePriceDrawer = ({ onClose, open, price }: Props) => {
  const [updatePrice] = useUpdatePriceMutation();
  const [loading, setLoading] = useState<boolean>(false);
  const [name, setName] = useState<string>(price.name);
  const [displayName, setDisplayName] = useState<string | null>(
    price.display_name ?? null,
  );
  const [amount, setAmount] = useState<number>(price.amount);
  const [defaultPrice, setDefaultPrice] = useState<boolean>(
    price.default_price
  );
  const [defaultSelection, setDefaultSelection] = useState<boolean>(
    price.default_selection
  );
  const [hasTitration, setHasTitration] = useState<boolean>(
    price.has_titration,
  );
  const [amountLimit, setAmountLimit] = useState<number | null>(
    price.amount_limit ?? null
  );
  const [competitorPrice, setCompetitorPrice] = useState<number | null>(
    price.competitor_price ?? null
  );
  const [monthlyPrice, setMonthlyPrice] = useState<number | null>(
    price.monthly_price ?? null
  );
  const [firstMonthPrice, setFirstMonthPrice] = useState<number | null>(
    price.first_month_price ?? null
  );
  const [defaultCouponId, setDefaultCouponId] = useState<number | "">(
    price.coupon?.id ?? ""
  );
  const errLog = useSentryLogger("error");

  const { data: coupons = [] } = useGetCouponsForProductQuery(
    price.product.slug
  );

  const handlePriceName = useCallback(
    (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setName(e.target.value);
    },
    []
  );

  const handleDisplayName = useCallback(
    (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setDisplayName(e.target.value);
    },
    [],
  );

  const handleAmount = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setAmount(Number(e.target.value));
  }, []);

  const handleDefaultPrice = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setDefaultPrice(e.target.checked);
  }, []);

  const handleDefaultSelection = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setDefaultSelection(e.target.checked);
    },
    []
  );

  const handleHasTitration = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setHasTitration(e.target.checked);
  }, []);

  const handleDefaultCoupon = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const v = e.target.value;

      setDefaultCouponId(v === "" ? "" : Number(v));
    },
    []
  );

  const handleSubmit = useCallback(
    async (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();

      setLoading(true);

      try {
        await updatePrice({
          id: price.id,
          amount,
          default_selection: defaultSelection,
          default_price: defaultPrice,
          name,
          amount_limit: amountLimit,
          competitor_price: competitorPrice,
          monthly_price: monthlyPrice,
          first_month_price: firstMonthPrice,
          default_coupon_id: defaultCouponId === "" ? null : defaultCouponId,
          display_name: displayName,
          has_titration: hasTitration,
        });
      } catch (err) {
        errLog("Error update price", err);
      } finally {
        setLoading(false);
        onClose();
      }
    },
    [
      amount,
      amountLimit,
      competitorPrice,
      defaultCouponId,
      defaultPrice,
      defaultSelection,
      displayName,
      errLog,
      firstMonthPrice,
      hasTitration,
      monthlyPrice,
      name,
      onClose,
      price.id,
      updatePrice,
    ]
  );

  useEffect(() => {
    setName(price.name);
    setDisplayName(price.display_name);
    setDefaultPrice(price.default_price);
    setDefaultSelection(price.default_selection);
    setAmount(price.amount);
    setDefaultCouponId(price.coupon?.id ?? "");
    setAmountLimit(price.amount_limit);
    setCompetitorPrice(price.competitor_price);
    setMonthlyPrice(price.monthly_price);
    setFirstMonthPrice(price.first_month_price);
    setHasTitration(price.has_titration);
  }, [
    price.amount,
    price.amount_limit,
    price.competitor_price,
    price.coupon?.id,
    price.default_coupon_id,
    price.default_price,
    price.default_selection,
    price.display_name,
    price.first_month_price,
    price.has_titration,
    price.interval,
    price.interval_count,
    price.monthly_price,
    price.name,
    price.number_of_months,
  ]);

  return (
    <DrawerForm
      label="Update Price"
      onClose={onClose}
      open={open}
      handleSubmit={handleSubmit}
      loading={loading}
    >
      <Stack gap={1}>
        <Typography variant="h6">Update price</Typography>
        <Divider />
      </Stack>
      <DrawerFieldRow type="input" label="id">
        <TextField disabled value={price.id} size="small" />
      </DrawerFieldRow>
      <DrawerFieldRow type="input" label="name">
        <TextField
          value={name}
          onChange={handlePriceName}
          multiline
          fullWidth
        />
      </DrawerFieldRow>
      <DrawerFieldRow type="input" label="display name">
        <TextField
          value={displayName}
          onChange={handleDisplayName}
          multiline
          fullWidth
        />
      </DrawerFieldRow>
      <DrawerFieldRow type="input" label="amount ($)">
        <TextField value={amount} onChange={handleAmount} type="number" />
      </DrawerFieldRow>
      <DrawerFieldRow type="input" label="stripe id">
        <TextField disabled value={price.stripe_id} size="small" multiline />
      </DrawerFieldRow>
      <DrawerFieldRow type="input" label="product id">
        <TextField disabled value={price.product_id} size="small" />
      </DrawerFieldRow>
      <DrawerFieldRow type="input" label="interval">
        <TextField disabled value={price.interval} />
      </DrawerFieldRow>
      <DrawerFieldRow type="input" label="interval count">
        <TextField disabled value={price.interval_count} type="number" />
      </DrawerFieldRow>
      <DrawerFieldRow label="number of months" type="input">
        <TextField disabled value={price.number_of_months} type="number" />
      </DrawerFieldRow>
      <DrawerFieldRow label="Default selection" type="checkbox">
        <Checkbox
          checked={defaultSelection}
          onChange={handleDefaultSelection}
          sx={{
            padding: 0,
            "&.Mui-checked": {
              color: "#FF8175",
            },
          }}
        />
      </DrawerFieldRow>
      <DrawerFieldRow label="Has titration" type="checkbox">
        <Checkbox
          checked={hasTitration}
          onChange={handleHasTitration}
          sx={{
            padding: 0,
            "&.Mui-checked": {
              color: "#FF8175",
            },
          }}
        />
      </DrawerFieldRow>
      <DrawerFieldRow label="Default price" type="checkbox">
        <Checkbox
          checked={defaultPrice}
          onChange={handleDefaultPrice}
          sx={{
            padding: 0,
            "&.Mui-checked": {
              color: "#FF8175",
            },
          }}
        />
      </DrawerFieldRow>
      <Stack gap={1}>
        <Typography variant="subtitle1">Optional fields</Typography>
        <Divider />
      </Stack>
      <DrawerFieldRow type="input" label="amount limit ($)">
        <TextField
          value={amountLimit}
          onChange={(e) => setAmountLimit(Number(e.target.value))}
        />
      </DrawerFieldRow>
      <DrawerFieldRow type="input" label="competitor price ($)">
        <TextField
          value={competitorPrice}
          onChange={(e) => setCompetitorPrice(Number(e.target.value))}
        />
      </DrawerFieldRow>
      <DrawerFieldRow type="input" label="monthly price ($)">
        <TextField
          value={monthlyPrice}
          onChange={(e) => setMonthlyPrice(Number(e.target.value))}
        />
      </DrawerFieldRow>
      <DrawerFieldRow type="input" label="first moth price ($)">
        <TextField
          value={firstMonthPrice}
          onChange={(e) => setFirstMonthPrice(Number(e.target.value))}
        />
      </DrawerFieldRow>
      <DrawerFieldRow type="input" label="default coupon">
        <TextField
          value={defaultCouponId}
          select
          onChange={handleDefaultCoupon}
        >
          <MenuItem value={""}>None</MenuItem>
          {coupons.map((coupon) => (
            <MenuItem
              key={coupon.id}
              value={coupon.id}
            >{`${coupon.name} ($${coupon.amount})`}</MenuItem>
          ))}
        </TextField>
      </DrawerFieldRow>
    </DrawerForm>
  );
};

export default UpdatePriceDrawer;
